import React, { useState, useRef } from 'react';
import axios from 'axios';
import Header from '../components/header';
import Footer from '../components/footer';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import 'bootstrap/dist/css/bootstrap.min.css';
import moment from 'moment';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';

function Liturgycalendar() {
    const [showModal, setShowModal] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const fullDataRef = useRef(null);
    const ongoingFetchesRef = useRef({});
    const firstLoadRef = useRef(true);

    const tryParse = (raw) => {
        if (!raw) return null;
        const m = moment(raw, 'YYYY-MM-DD hh:mm A', true);
        if (!m.isValid()) return null;
        return { m };
    };

    const fetchFullUpcoming = async () => {
        if (fullDataRef.current) return fullDataRef.current;

        try {
            const resp = await axios.get(
                'https://parish.cristolive.org/api/liturgical/24251/upcoming'
            );

            const arr = resp?.data?.data ?? [];
            fullDataRef.current = arr;
            return arr;
        } catch (e) {
            console.error(e);
            return [];
        }
    };

    const filterAndParse = (items, start, end) => {
        const startM = moment(start);
        const endM = moment(end);

        return items
            .map(item => {
                const parsed = tryParse(item.start);
                if (!parsed) return null;

                const date = parsed.m;

                if (!date.isBetween(startM, endM, null, '[)')) return null;

                const formattedTime = date.format('hh:mm A');
                const language = item.language_id?.[1] || 'Unknown';

                return {
                    id: item.id,
                    title: item.name,
                    start: date.toISOString(),
                    end: item.stop
                        ? moment(item.stop, 'YYYY-MM-DD hh:mm A').toISOString()
                        : null,
                    allDay: false,
                    extendedProps: {
                        name: item.name,
                        language,
                        time: formattedTime
                    },
                    datetime: date.valueOf()
                };
            })
            .filter(Boolean)
            .sort((a, b) => a.datetime - b.datetime);
    };

    const fetchEventsForRange = async (startStr, endStr) => {
        const key = `${startStr}_${endStr}`;

        if (ongoingFetchesRef.current[key]) {
            return ongoingFetchesRef.current[key];
        }

        const promise = (async () => {
            const items = await fetchFullUpcoming();
            return filterAndParse(items, startStr, endStr);
        })();

        ongoingFetchesRef.current[key] = promise;

        try {
            return await promise;
        } finally {
            delete ongoingFetchesRef.current[key];
        }
    };

    const fetchEvents = async (fetchInfo, successCallback, failureCallback) => {
        try {
            if (firstLoadRef.current) setIsLoading(true);

            const events = await fetchEventsForRange(
                fetchInfo.startStr,
                fetchInfo.endStr
            );

            successCallback(events);
            setIsLoading(false);
            firstLoadRef.current = false;
        } catch (e) {
            setIsLoading(false);
            failureCallback(e);
        }
    };

    const openModal = (info) => {
        setSelectedEvent(info.event);
        setShowModal(true);
    };

    return (
        <>
            <Header />

            <div className="container-fluid px-4 mt-4">
                <div className="card shadow-sm p-3">

                    <FullCalendar
                        plugins={[
                            dayGridPlugin,
                            timeGridPlugin,
                            listPlugin,
                            interactionPlugin
                        ]}
                        events={fetchEvents}
                        initialView="dayGridMonth"
                        eventClick={openModal}
                        eventContent={renderEventContent}
                        height="auto"
                        dayMaxEventRows={false}   // ✅ show all like timetable
                    />

                </div>

                {isLoading && (
                    <div className="text-center mt-3">
                        <div className="spinner-border text-primary" />
                    </div>
                )}
            </div>

            <Footer />

            <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {selectedEvent?.extendedProps?.name}
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <p><b>Language:</b> {selectedEvent?.extendedProps?.language}</p>
                    <p><b>Time:</b> {selectedEvent?.extendedProps?.time}</p>

                    <div className="text-end">
                        <Button
                            onClick={() =>
                                window.open(
                                    'https://massbooking.shrinebasilicabangalore.org/',
                                    '_blank'
                                )
                            }
                        >
                            Book Mass
                        </Button>
                    </div>
                </Modal.Body>
            </Modal>

            {/* ✅ EXACT TIMETABLE STYLE */}
            <style>{`
                .fc {
                    font-family: 'Segoe UI', sans-serif;
                }

                .fc-daygrid-day {
                    min-height: 120px;
                }

                .fc-daygrid-day-frame {
                    padding: 4px;
                }

                .fc-daygrid-event {
                    width: 100% !important;
                    display: block !important;
                    margin-bottom: 4px;
                    border: none !important;
                    background: transparent !important;
                }

                .fc-daygrid-day-events {
                    margin-top: 5px;
                }

                .fc-toolbar-title {
                    font-weight: bold;
                }
            `}</style>
        </>
    );
}

// ✅ TIMETABLE STYLE EVENTS (MATCH IMAGE)
function renderEventContent(eventInfo) {
    const { language, time } = eventInfo.event.extendedProps;

    let bg = '#b7e4c7'; // English
    if (language === 'Tamil') bg = '#a9def9';
    if (language === 'Kannada') bg = '#f9e0a9';

    return (
        <div
            style={{
                background: bg,
                padding: '6px 8px',
                borderRadius: '4px',
                fontSize: '13px',
                fontWeight: 500,
                width: '100%',
                borderLeft: '4px solid #2563eb'
            }}
        >
            {language} - {time}
        </div>
    );
}

export default Liturgycalendar;
